import { useEffect, useState, type FormEvent } from "react";
import {
  Button,
  Checkbox,
  Input,
  Select,
  type SelectItem,
  type SelectOption,
} from "@legal-500/legal500-design-system";
import {
  FIRM_LOGO_FILL_KINDS,
  type FirmLogoFillSummary,
} from "../../../api/firmLogoFill.service.js";
import { WEBSITE_OPTIONS, type Website } from "../../../constants/features.js";
import {
  KIND_LABELS,
  OTHER_OUTCOMES_LABEL,
  otherReasonsOf,
  reasonTextOf,
  REASONS_BY_KIND,
} from "./outcomeLabels.js";
import type { FilterChanges } from "./useFirmLogoCopyFilters.js";
import styles from "./FirmLogoCopy.module.css";

const ALL = "all";

type FirmLogoCopyFiltersProps = {
  reason: string | undefined;
  website: Website | undefined;
  company: string | undefined;
  unresolved: boolean | undefined;
  summary: FirmLogoFillSummary | null;
  onChange: (changes: FilterChanges) => void;
};

function reasonOptionsFor(
  reasons: readonly string[],
  summary: FirmLogoFillSummary | null,
): SelectOption[] {
  return reasons.map(value => {
    const label = reasonTextOf(value).label;

    return {
      value,
      label: summary ? `${label} (${summary.byReason[value] ?? 0})` : label,
    };
  });
}

export function FirmLogoCopyFilters({
  reason,
  website,
  company,
  unresolved,
  summary,
  onChange,
}: FirmLogoCopyFiltersProps) {
  const [companyInput, setCompanyInput] = useState(company ?? "");

  useEffect(() => {
    setCompanyInput(company ?? "");
  }, [company]);

  const submitCompany = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onChange({ company: companyInput.trim() });
  };

  const otherReasons = otherReasonsOf(summary?.byReason, reason);

  const reasonOptions: SelectItem[] = [
    { value: ALL, label: summary ? `All outcomes (${summary.totalRows})` : "All outcomes" },
    ...FIRM_LOGO_FILL_KINDS.map(kind => ({
      label: KIND_LABELS[kind],
      options: reasonOptionsFor(REASONS_BY_KIND[kind], summary),
    })),
    ...(otherReasons.length > 0
      ? [{ label: OTHER_OUTCOMES_LABEL, options: reasonOptionsFor(otherReasons, summary) }]
      : []),
  ];

  const websiteOptions: SelectItem[] = [
    { value: ALL, label: "All websites" },
    ...WEBSITE_OPTIONS.map(option => ({ value: option.value, label: option.label })),
  ];

  return (
    <div className={styles.filters}>
      <Select
        id="firm-logo-copy-reason"
        label="Outcome"
        value={reason ?? ALL}
        options={reasonOptions}
        onChange={event =>
          onChange({
            reason: event.target.value === ALL ? undefined : event.target.value,
          })
        }
      />
      <Select
        id="firm-logo-copy-website"
        label="Website"
        value={website ?? ALL}
        options={websiteOptions}
        onChange={event =>
          onChange({
            website: event.target.value === ALL ? undefined : event.target.value,
          })
        }
      />
      <form className={styles.companyForm} onSubmit={submitCompany}>
        <Input
          id="firm-logo-copy-company"
          label="Firm name or ID"
          type="search"
          value={companyInput}
          onChange={event => setCompanyInput(event.target.value)}
        />
        <Button type="submit" variant="outlined">
          Search
        </Button>
        {company !== undefined ? (
          <Button type="button" variant="text" onClick={() => onChange({ company: "" })}>
            Clear
          </Button>
        ) : null}
      </form>
      <Checkbox
        id="firm-logo-copy-unresolved"
        label="Unresolved only"
        aria-label="Unresolved only"
        checked={unresolved === true}
        onChange={event => onChange({ unresolved: event.target.checked ? "true" : undefined })}
      />
    </div>
  );
}
